import React from "react";
import BlogHero from "../blog/BlogHero";
import BlogQuote from "../blog/BlogQuote";
import BlogTip from "../blog/BlogTip";
import BlogImage from "../blog/BlogImage";
import BlogHeader from "../blog/BlogHeader";
import BlogFooter from "../blog/BlogFooter";

import {
	Smartphone,
	ShoppingCart,
	Gift,
	BarChart3,
	Users,
	Star,
} from "lucide-react";
import {
	Phone,
	CreditCard,
	FileX,
	Clock,
	CheckCircle2,
	Zap,
	CalendarCheck,
} from "lucide-react";

import heroImg from "../../assets/blog/beneficios-hero.jpg";
import ejemploImg from "../../assets/blog/beneficios-ejemplo.jpg";

export default function PostBeneficios() {
	return (
		<div className="font-poppins text-gray-800">
			{/* Header fijo del blog */}
			<BlogHeader />

			{/* Hero principal */}
			<BlogHero
				title="Beneficios de tener tu propia app para tu negocio"
				subtitle="Más control, más clientes, más ingresos. Descubre por qué cada vez más negocios —desde restaurantes a barberías— apuestan por tener su propia aplicación."
				image={heroImg}
			/>

			<main className="max-w-4xl mx-auto px-6 py-12 space-y-16">
				{/* Introducción */}
				<section>
					<p className="text-lg leading-relaxed mb-6">
						Antes, un negocio dependía casi exclusivamente de su ubicación
						física y del boca a boca. Hoy, los clientes esperan poder reservar
						una cita, pedir productos o acceder a promociones directamente desde
						su móvil.
					</p>
					<p className="text-lg leading-relaxed">
						Una <strong>app propia</strong> te da independencia de las grandes
						plataformas y una relación mucho más directa con tus clientes.
					</p>
				</section>

				{/* Beneficios principales */}
				<section>
					<h2 className="text-2xl font-bold mb-8">
						Principales beneficios de tu propia app
					</h2>
					<ul className="space-y-6 text-lg text-gray-700">
						<li className="flex items-start">
							<div className="w-9 h-9 flex items-center justify-center rounded-full  mr-3">
								<Smartphone className="w-6 h-6 text-[#43bd98] mr-3 mt-1" />
							</div>
							<div>
								<strong>Control total de tu marca:</strong> tu logo, tus
								colores, tu identidad. Sin aparecer junto a cientos de
								competidores en plataformas genéricas.
							</div>
						</li>
						<li className="flex items-start">
							<div className="w-9 h-9 flex items-center justify-center rounded-full  mr-3">
								<ShoppingCart className="w-6 h-6 text-[#43bd98] mr-3 mt-1" />
							</div>
							<div>
								<strong>Ventas directas sin comisiones:</strong> ahorra el
								15–30% que cobran las plataformas externas en pedidos o
								reservas.
							</div>
						</li>
						<li className="flex items-start">
							<div className="w-9 h-9 flex items-center justify-center rounded-full  mr-3">
								<Gift className="w-6 h-6 text-[#43bd98] mr-3 mt-1" />
							</div>
							<div>
								<strong>Fidelización digital:</strong> notificaciones push,
								cupones, bonos y programas de puntos para que tus clientes
								siempre vuelvan.
							</div>
						</li>
						<li className="flex items-start">
							<div className="w-9 h-9 flex items-center justify-center rounded-full  mr-3">
								<Users className="w-6 h-6 text-[#43bd98] mr-3 mt-1" />
							</div>
							<div>
								<strong>Mejor experiencia del cliente:</strong> reservas,
								pedidos o compras fáciles desde cualquier dispositivo, en
								segundos.
							</div>
						</li>
						<li className="flex items-start">
							<div className="w-9 h-9 flex items-center justify-center rounded-full  mr-3">
								<BarChart3 className="w-6 h-6 text-[#43bd98] mr-3 mt-1" />
							</div>
							<div>
								<strong>Datos valiosos:</strong> conoce mejor a tus clientes,
								sus hábitos de consumo y preferencias para tomar mejores
								decisiones.
							</div>
						</li>
						<li className="flex items-start">
							<div className="w-9 h-9 flex items-center justify-center rounded-full  mr-3">
								<Star className="w-6 h-6 text-[#43bd98] mr-3 mt-1" />
							</div>
							<div>
								<strong>Más recurrencia y ticket medio:</strong> promociones
								personalizadas y recordatorios automáticos que generan ventas
								adicionales.
							</div>
						</li>
					</ul>
				</section>

				<section>
					<h2 className="text-2xl font-bold mb-4">
						Cómo diferentes negocios aprovechan su app
					</h2>
					<p className="text-lg leading-relaxed mb-6">
						Cada sector utiliza su aplicación de forma distinta. Estos son
						algunos ejemplos de cómo sacar partido a tu app:
					</p>
					<ul className="space-y-4 text-lg text-gray-700">
						<li>
							<strong>Restaurantes:</strong> pedidos online, reservas y
							promociones en tiempo real.
						</li>
						<li>
							<strong>Peluquerías y barberías:</strong> gestión de citas y
							recordatorios automáticos.
						</li>
						<li>
							<strong>Tiendas online:</strong> venta directa, cupones y
							notificaciones de nuevos productos.
						</li>
						<li>
							<strong>Entrenadores personales:</strong> reservas de sesiones y
							envío de planes personalizados.
						</li>
					</ul>
				</section>

				<section className="bg-gray-50 rounded-2xl p-8 shadow-sm">
					<h2 className="text-2xl font-bold mb-6 text-gray-900 text-center">
						Antes vs Después de tener tu propia app
					</h2>

					<div className="grid md:grid-cols-2 gap-8">
						{/* Antes */}
						<div className="bg-white border border-gray-200 rounded-xl p-6">
							<h3 className="text-xl font-semibold text-gray-900 mb-4">
								Antes
							</h3>
							<ul className="space-y-4 text-gray-700">
								<li className="flex items-start gap-3">
									<Phone className="w-5 h-5 text-gray-400 mt-1" />
									<span>
										Reservas solo por teléfono, con riesgo de errores.
									</span>
								</li>
								<li className="flex items-start gap-3">
									<CreditCard className="w-5 h-5 text-gray-400 mt-1" />
									<span>Comisiones del 15–30 % en plataformas externas.</span>
								</li>
								<li className="flex items-start gap-3">
									<FileX className="w-5 h-5 text-gray-400 mt-1" />
									<span>Sin datos de clientes ni hábitos de compra.</span>
								</li>
								<li className="flex items-start gap-3">
									<Clock className="w-5 h-5 text-gray-400 mt-1" />
									<span>Procesos manuales que consumen tiempo.</span>
								</li>
							</ul>
						</div>

						{/* Después */}
						<div className="bg-white border border-[#43bd98]/40 rounded-xl p-6">
							<h3 className="text-xl font-semibold text-[#43bd98] mb-4">
								Después
							</h3>
							<ul className="space-y-4 text-gray-700">
								<li className="flex items-start gap-3">
									<CalendarCheck className="w-5 h-5 text-[#43bd98] mt-1" />
									<span>Reservas y pedidos online en segundos.</span>
								</li>
								<li className="flex items-start gap-3">
									<CheckCircle2 className="w-5 h-5 text-[#43bd98] mt-1" />
									<span>Sin pagar comisiones a terceros.</span>
								</li>
								<li className="flex items-start gap-3">
									<BarChart3 className="w-5 h-5 text-[#43bd98] mt-1" />
									<span>Acceso a estadísticas y hábitos de clientes.</span>
								</li>
								<li className="flex items-start gap-3">
									<Zap className="w-5 h-5 text-[#43bd98] mt-1" />
									<span>Automatización que ahorra tiempo al equipo.</span>
								</li>
							</ul>
						</div>
					</div>
				</section>
				<BlogImage
					src={ejemploImg}
					alt="Diseño profesional de aplicaciones"
					caption="En Applaby dedicamos tiempo a cuidar la experiencia de usuario y el diseño visual."
				/>

				{/* Consejo destacado */}
				<BlogTip text="Digitalizar no significa complicar. Cuanto más simple sea tu app para tus clientes y tu equipo, mejores resultados obtendrás." />

				<section>
					<h2 className="text-2xl font-bold mb-4">
						Integraciones y compatibilidad
					</h2>
					<p className="text-lg leading-relaxed mb-6">
						Una app propia no solo mejora la experiencia de tus clientes,
						también se adapta al funcionamiento real de tu negocio. Con Applaby
						disfrutas de integraciones y funciones listas para usar desde el
						primer día:
					</p>

					<ul className="space-y-4 text-lg text-gray-700">
						<li className="flex items-start gap-2">
							<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
							<span>
								<strong>Pagos online seguros:</strong> integración con pasarelas
								de pago para cobrar sin complicaciones.
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
							<span>
								<strong>Reservas automáticas:</strong> gestión de citas y mesas
								en tiempo real, sin llamadas ni agendas en papel.
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
							<span>
								<strong>Notificaciones instantáneas:</strong> comunica
								novedades, ofertas o recordatorios en segundos.
							</span>
						</li>
						<li className="flex items-start gap-2">
							<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
							<span>
								<strong>Compatible:</strong> tus clientes pueden usar la app en
								iOS, Android o directamente desde la web.
							</span>
						</li>
					</ul>
				</section>

				<section>
					<h2 className="text-2xl font-bold mb-4">
						¿Es realmente caro tener una app?
					</h2>

					<p className="text-lg leading-relaxed mb-6 text-gray-700">
						Hace unos años desarrollar una app propia implicaba miles de euros y
						meses de trabajo. Hoy, con Applaby, puedes lanzar una app
						profesional sin inversión inicial y con una cuota mensual ajustada a
						tu sector.
					</p>

					{/* Callout de precio */}
					<div className="bg-white border border-[#43bd98]/30 rounded-2xl p-6 shadow-sm mb-6">
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
							<div>
								<h3 className="text-xl font-bold text-gray-900">
									Desde{" "}
									<span className="text-[#43bd98]">29,90 € + IVA/mes</span>
								</h3>
								<p className="text-sm text-gray-500">
									Puede variar según el sector
								</p>
							</div>

							<a
								href="/#precios"
								className="inline-block bg-[#43bd98] text-white px-5 py-2.5 rounded-lg font-medium shadow hover:bg-[#37a885] transition"
							>
								Ver planes y sectores
							</a>
						</div>

						<ul className="mt-5 grid sm:grid-cols-2 gap-y-4 gap-x-12 text-gray-700">
							<li className="flex items-start gap-2">
								<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
								<span>Sin inversión inicial</span>
							</li>
							<li className="flex items-start gap-2">
								<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
								<span>App + web listas en pocos días</span>
							</li>
							<li className="flex items-start gap-2">
								<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
								<span>Actualizaciones y soporte incluidos</span>
							</li>
							<li className="flex items-start gap-2">
								<span className="mt-2 inline-block h-2 w-2 rounded-full bg-[#43bd98]" />
								<span>Funcionalidades según sector</span>
							</li>
						</ul>
					</div>

					<p className="text-lg leading-relaxed text-gray-700">
						Con Applaby, tener tu propia app deja de ser un lujo para
						convertirse en una herramienta real y asequible para tu negocio.
					</p>
				</section>

				{/* Conclusión */}

				<div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
					<h3 className="text-xl font-semibold mb-3 text-gray-900">
						¿Listo para dar el paso?
					</h3>
					<p className="text-gray-600 mb-5">
						Solicita una demo gratuita y descubre cómo tu negocio puede crecer
						gracias a la digitalización.
					</p>
					<a
						href="https://applaby.com/app-demo/"
						target="_blank"
						rel="noreferrer"
						className="inline-block bg-[#43bd98] text-white px-6 py-3 rounded-lg font-medium shadow hover:bg-[#37a885] transition"
					>
						Solicitar demo gratis
					</a>
				</div>
			</main>

			<BlogFooter />
		</div>
	);
}
